import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const testimonials = [
  {
    name: 'Sarah M.',
    role: 'Entrepreneur',
    product: 'RUSH',
    content: 'RUSH has completely transformed my morning routine. The focus is clean and sustained without any jitters.',
    rating: 5,
  },
  {
    name: 'David L.',
    role: 'Software Engineer',
    product: 'SYNAPTIQ',
    content: 'SYNAPTIQ is my go-to for deep work sessions. The cognitive clarity is unlike anything I\'ve tried.',
    rating: 5,
  },
  {
    name: 'Emma K.',
    role: 'Yoga Instructor',
    product: 'VITRA',
    content: 'VITRA helps me maintain balance throughout my busy days. I feel more grounded and energized.',
    rating: 5,
  },
  {
    name: 'Michael R.',
    role: 'Creative Director',
    product: 'RUSH',
    content: 'The quality of these supplements is exceptional. You can feel the difference from the first dose.',
    rating: 5,
  },
  {
    name: 'Lisa T.',
    role: 'Medical Professional',
    product: 'SYNAPTIQ',
    content: 'Finally, supplements backed by real science. I recommend Berleto to all my health-conscious patients.',
    rating: 5,
  },
  {
    name: 'James W.',
    role: 'Personal Trainer',
    product: 'VITRA',
    content: 'My clients notice the difference immediately. Cleaner energy, sharper thinking, better recovery.',
    rating: 5,
  },
  {
    name: 'Nina S.',
    role: 'Architect',
    product: 'RUSH',
    content: 'Three months in and my productivity has shifted completely. This is what real optimization feels like.',
    rating: 5,
  },
  {
    name: 'Tom B.',
    role: 'Physician',
    product: 'SYNAPTIQ',
    content: 'The formulation is thoughtful and evidence-based. Rare in this space. I use it myself daily.',
    rating: 5,
  },
];

function StarRating({ count }) {
  return (
    <div className="flex gap-0.5 mb-4">
      {Array.from({ length: count }).map((_, i) => (
        <svg key={i} width="12" height="12" viewBox="0 0 12 12" fill="none">
          <path
            d="M6 1L7.34 4.26L11 4.74L8.5 7.14L9.18 11L6 9.28L2.82 11L3.5 7.14L1 4.74L4.66 4.26L6 1Z"
            fill="#B8704D"
          />
        </svg>
      ))}
    </div>
  );
}

function TestimonialCard({ t }) {
  return (
    <div
      className="flex-shrink-0 w-72 mx-3 p-6 rounded-xl"
      style={{ background: '#FAF9F6', boxShadow: '0 2px 20px rgba(26,42,26,0.06)' }}
    >
      <StarRating count={t.rating} />
      <p
        className="text-sm leading-relaxed mb-5"
        style={{ color: '#4A5A4A' }}
      >
        "{t.content}"
      </p>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium" style={{ color: '#1A2A1A' }}>
            {t.name}
          </p>
          <p className="text-xs mt-0.5" style={{ color: '#8B9A8B' }}>
            {t.role}
          </p>
        </div>
        <span
          className="text-xs font-medium tracking-widest px-2 py-1 rounded"
          style={{ background: 'rgba(184,112,77,0.1)', color: '#B8704D' }}
        >
          {t.product}
        </span>
      </div>
    </div>
  );
}

function MarqueeRow({ items, reverse = false }) {
  const duplicated = [...items, ...items];
  return (
    <div className="overflow-hidden">
      <div
        className={`flex ${reverse ? 'marquee-reverse' : 'marquee'}`}
        style={{ width: 'max-content' }}
      >
        {duplicated.map((t, i) => (
          <TestimonialCard key={i} t={t} />
        ))}
      </div>
    </div>
  );
}

export function TestimonialsSection() {
  const headerRef = useRef(null);
  const inView = useInView(headerRef, { once: true, margin: '-60px' });

  const row1 = testimonials.slice(0, 4);
  const row2 = testimonials.slice(4, 8);

  return (
    <>
      <style>{`
        @keyframes marquee {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @keyframes marquee-reverse {
          from { transform: translateX(-50%); }
          to   { transform: translateX(0); }
        }
        .marquee {
          animation: marquee 32s linear infinite;
        }
        .marquee-reverse {
          animation: marquee-reverse 28s linear infinite;
        }
        .marquee:hover,
        .marquee-reverse:hover {
          animation-play-state: paused;
        }
      `}</style>

      <section
        className="py-24 overflow-hidden"
        style={{ background: '#E8DCC4' }}
      >
        {/* Header */}
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 28 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="text-center px-6 mb-16"
        >
          <p
            className="text-xs font-medium tracking-[0.25em] uppercase mb-4"
            style={{ color: '#B8704D' }}
          >
            Community
          </p>
          <h2
            className="text-4xl md:text-5xl font-light"
            style={{ color: '#1A2A1A' }}
          >
            What people are saying
          </h2>
        </motion.div>

        {/* Marquee rows */}
        <div className="flex flex-col gap-4">
          <MarqueeRow items={row1} reverse={false} />
          <MarqueeRow items={row2} reverse={true} />
        </div>

        {/* Bottom stat bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="flex justify-center gap-12 mt-16 px-6"
        >
          {[
            { value: '12,000+', label: 'Happy customers' },
            { value: '4.9 / 5', label: 'Average rating' },
            { value: '94%', label: 'Would recommend' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-2xl font-light" style={{ color: '#1A2A1A' }}>
                {stat.value}
              </p>
              <p className="text-xs tracking-widest uppercase mt-1" style={{ color: '#6B5B54' }}>
                {stat.label}
              </p>
            </div>
          ))}
        </motion.div>
      </section>
    </>
  );
}
